/**
 * Admin "Teachers" panel — list, create (shows one-time temp pw to copy),
 * reset password, enable/disable, delete.
 */
import React, { useEffect, useState } from "react";
import { api } from "../api";

export default function TeachersPanel({ token }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [credentialsToShow, setCredentialsToShow] = useState(null);

  async function refresh() {
    setLoading(true);
    setError("");
    try {
      setList(await api.adminListTeachers(token));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  async function toggleActive(t) {
    try {
      const updated = await api.adminUpdateTeacher(token, t.id, { active: !t.active });
      setList((p) => p.map((x) => (x.id === t.id ? updated : x)));
    } catch (err) {
      alert(err.message);
    }
  }

  async function resetPw(t) {
    if (!window.confirm(`Generate a new temporary password for ${t.name}?`)) return;
    try {
      const r = await api.adminResetTeacherPassword(token, t.id);
      setCredentialsToShow({
        name: r.teacher.name,
        email: r.teacher.email,
        temp_password: r.temp_password,
      });
      setList((p) => p.map((x) => (x.id === t.id ? r.teacher : x)));
    } catch (err) {
      alert(err.message);
    }
  }

  async function remove(t) {
    if (!window.confirm(`Delete ${t.name}? (only allowed if no lessons assigned)`)) return;
    try {
      await api.adminDeleteTeacher(token, t.id);
      setList((p) => p.filter((x) => x.id !== t.id));
    } catch (err) {
      alert(err.message);
    }
  }

  function copy(v) { navigator.clipboard?.writeText(v).catch(() => {}); }

  return (
    <section className="mt-12" data-testid="admin-teachers-panel">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-2">
            Teachers
          </div>
          <h2 className="font-display text-3xl text-[#1C1C1C] tracking-tighter font-light">
            Roster
          </h2>
        </div>
        <button
          data-testid="admin-teachers-add-btn"
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
        >
          + Add teacher
        </button>
      </div>

      {error && <div className="mb-3 text-sm text-[#B83A3A]">{error}</div>}

      {loading ? (
        <div className="text-[#1C1C1C]/60">Loading…</div>
      ) : list.length === 0 ? (
        <div className="p-6 bg-white border border-[#1C1C1C]/10 rounded-2xl text-center text-[#1C1C1C]/60" data-testid="admin-teachers-empty">
          No teachers yet — add one to start assigning lessons.
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((t) => (
            <div
              key={t.id}
              data-testid={`admin-teacher-row-${t.id}`}
              className="flex flex-wrap items-center justify-between gap-3 bg-white border border-[#1C1C1C]/10 rounded-xl px-5 py-3"
            >
              <div className="min-w-0">
                <div className="font-display text-base text-[#1C1C1C]">{t.name}</div>
                <div className="text-xs text-[#1C1C1C]/55 font-mono mt-0.5">{t.email}</div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/45 mt-1">
                  {t.lesson_count} lesson{t.lesson_count === 1 ? "" : "s"} · {t.active ? "active" : "disabled"}
                  {t.must_change_password ? " · temp password" : ""}
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                <button onClick={() => resetPw(t)} data-testid={`admin-teacher-reset-${t.id}`}
                  className="px-2 py-1 text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/65 hover:text-[#1C1C1C]">
                  Reset password
                </button>
                <button onClick={() => toggleActive(t)} data-testid={`admin-teacher-toggle-${t.id}`}
                  className="px-2 py-1 text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/65 hover:text-[#1C1C1C]">
                  {t.active ? "Disable" : "Enable"}
                </button>
                <button onClick={() => remove(t)} data-testid={`admin-teacher-delete-${t.id}`}
                  className="px-2 py-1 text-[10px] tracking-[0.22em] uppercase text-[#B83A3A]/80 hover:text-[#B83A3A]">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddTeacherModal
          token={token}
          onClose={() => setShowAdd(false)}
          onAdded={(r) => {
            setList((p) => [r.teacher, ...p]);
            setShowAdd(false);
            setCredentialsToShow({ name: r.teacher.name, email: r.teacher.email, temp_password: r.temp_password });
          }}
        />
      )}

      {credentialsToShow && (
        <CredentialsModal data={credentialsToShow} onClose={() => setCredentialsToShow(null)} onCopy={copy} />
      )}
    </section>
  );
}

function AddTeacherModal({ token, onClose, onAdded }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [bio, setBio] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const r = await api.adminCreateTeacher(token, {
        name,
        email,
        bio: bio || null,
      });
      onAdded(r);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="admin-teachers-modal">
      <div className="w-full max-w-md bg-white rounded-t-3xl md:rounded-3xl p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl text-[#1C1C1C]">Add teacher</h2>
          <button onClick={onClose} className="text-[#1C1C1C]/60 hover:text-[#1C1C1C]">✕</button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Name">
            <input data-testid="admin-teacher-name" required value={name} onChange={(e) => setName(e.target.value)} className="input" />
          </Field>
          <Field label="Email (used to sign in)">
            <input data-testid="admin-teacher-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input" placeholder="teacher@example.com" />
          </Field>
          <Field label="Short bio (optional)">
            <textarea data-testid="admin-teacher-bio" rows={3} value={bio} onChange={(e) => setBio(e.target.value)} className="input resize-y" />
          </Field>
          {error && <div className="text-sm text-[#B83A3A]" data-testid="admin-teacher-error">{error}</div>}
          <button data-testid="admin-teacher-submit" type="submit" disabled={loading}
            className="w-full px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors">
            {loading ? "Creating…" : "Create teacher"}
          </button>
        </form>
        <style>{`.input{width:100%;padding:.65rem 1rem;background:#FAF9F6;border:1px solid rgba(28,28,28,0.1);border-radius:.75rem;font-size:0.95rem;outline:none}.input:focus{border-color:rgba(28,28,28,0.4)}`}</style>
      </div>
    </div>
  );
}

function CredentialsModal({ data, onClose, onCopy }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" data-testid="admin-teacher-credentials-modal">
      <div className="w-full max-w-md bg-white rounded-3xl p-8">
        <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-2">
          Save these now
        </div>
        <h2 className="font-display text-2xl text-[#1C1C1C] mb-4">{data.name}'s credentials</h2>
        <p className="text-sm text-[#1C1C1C]/65 mb-6">
          The password is shown only once. Copy and send it to the teacher securely. They'll be prompted to change it on first sign-in.
        </p>
        <div className="space-y-3">
          <Row label="Email" value={data.email} onCopy={() => onCopy(data.email)} />
          <Row label="Temporary password" value={data.temp_password} onCopy={() => onCopy(data.temp_password)} mono />
        </div>
        <button onClick={onClose} data-testid="admin-teacher-credentials-done"
          className="mt-6 w-full px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors">
          I've saved them
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, onCopy }) {
  return (
    <div className="flex items-center justify-between gap-3 bg-[#FAF9F6] rounded-xl px-3 py-2.5">
      <div className="min-w-0">
        <div className="text-[9px] tracking-[0.28em] uppercase text-[#1C1C1C]/50">{label}</div>
        <div className="font-mono text-sm tracking-wider text-[#1C1C1C] truncate">{value}</div>
      </div>
      <button onClick={onCopy} className="text-[10px] tracking-[0.24em] uppercase text-[#1C1C1C]/60 hover:text-[#1C1C1C] px-2 py-1 rounded border border-[#1C1C1C]/10">
        Copy
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
